import { BarChart, Collection, CreditCard, FileEarmarkText, Gear, House, People } from 'react-bootstrap-icons';

import { PARTNER_ROUTES } from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSIcon } from '@repo/sds/SDSIcon';
import { Sidebar } from '@repo/ui/components/elements/Sidebar/Sidebar';

import {
  useCapabilities,
  useCapabilitiesLoaded,
  useCapabilitiesUnavailable,
} from './permissions/partner-capabilities-context';

const dashboardItem = {
  icon: <SDSIcon icon={<House />} />,
  title: 'Dashboard',
  path: PARTNER_ROUTES.PARTNER_DASHBOARD,
  testid: DataTestIds.PARTNER.SIDEBAR.DASHBOARD,
};

const membersItem = {
  icon: <SDSIcon icon={<People />} />,
  title: 'Members',
  path: PARTNER_ROUTES.PARTNER_MEMBERS,
  testid: DataTestIds.PARTNER.SIDEBAR.MEMBERS,
};

const cohortsItem = {
  icon: <SDSIcon icon={<Collection />} />,
  title: 'Cohorts',
  path: PARTNER_ROUTES.PARTNER_COHORTS,
  testid: DataTestIds.PARTNER.SIDEBAR.COHORTS,
};

const licenseItem = {
  icon: <SDSIcon icon={<FileEarmarkText />} />,
  title: 'License',
  path: PARTNER_ROUTES.PARTNER_LICENSE,
  testid: DataTestIds.PARTNER.SIDEBAR.LICENSE,
};

const billingItem = {
  icon: <SDSIcon icon={<CreditCard />} />,
  title: 'Billing',
  path: PARTNER_ROUTES.PARTNER_BILLING,
  testid: DataTestIds.PARTNER.SIDEBAR.BILLING,
};

const analyticsItem = {
  icon: <SDSIcon icon={<BarChart />} />,
  title: 'Analytics',
  path: PARTNER_ROUTES.PARTNER_ANALYTICS,
  testid: DataTestIds.PARTNER.SIDEBAR.ANALYTICS,
};

const settingsItem = {
  icon: <SDSIcon icon={<Gear />} />,
  title: 'Settings',
  path: PARTNER_ROUTES.PARTNER_SETTINGS,
  testid: DataTestIds.PARTNER.SIDEBAR.SETTINGS,
};

export function PartnerSidebar() {
  const capabilities = useCapabilities();
  const capabilitiesLoaded = useCapabilitiesLoaded();
  const capabilitiesUnavailable = useCapabilitiesUnavailable();

  // Members + Cohorts are capability-gated. While the snapshot loads, hold
  // both gated items out so STARTUP_TOOLS partners don't see them flash
  // before they disappear. The pages themselves also guard for deep-link
  // safety; backend remains the security boundary.
  //
  // During an outage every capability reads as denied for want of an answer,
  // not because the partner lacks it. Hiding on that would drop five links
  // beside a panel saying permissions could not be loaded — an outage
  // rendered as a permissions decision, which is what the panel exists to
  // stop. The links stay; clicking one lands on the same panel.
  const allows = (capability: boolean): boolean => capabilitiesUnavailable || (capabilitiesLoaded && capability);
  const showMembers = allows(capabilities.canDistributeInternally);
  // Cohorts nav is gated on canManageCohorts alone. A type that may assign
  // content but not manage cohorts (e.g. VC_FIRM) can still open a cohort
  // by deep link to withdraw grants, but the nav stays hidden for them.
  const showCohorts = allows(capabilities.canManageCohorts);
  const showLicense = allows(capabilities.canViewLicenses);
  // Billing surfaces the commercial side of the same licensing scope, so
  // it shares the License capability gate.
  const showBilling = showLicense;
  const showAnalytics = allows(capabilities.canViewAnalytics);
  const items = [
    dashboardItem,
    ...(showMembers ? [membersItem] : []),
    ...(showCohorts ? [cohortsItem] : []),
    ...(showLicense ? [licenseItem] : []),
    ...(showBilling ? [billingItem] : []),
    ...(showAnalytics ? [analyticsItem] : []),
    settingsItem,
  ];
  return <Sidebar items={items} />;
}
